/**
 * <live-stream-banner> — custom element for active live stream banner.
 */
if (!customElements.get('live-stream-banner')) {
  class LiveStreamBanner extends HTMLElement {
    static PLATFORM_BRANDS = {
      youtube: { background: '#FF0000', text: '#FFFFFF' },
      tiktok: { background: '#010101', text: '#FFFFFF' },
      facebook: { background: '#1877F2', text: '#FFFFFF' },
      instagram: { background: '#E1306C', text: '#FFFFFF' },
    };

    connectedCallback() {
      this.titleEl = this.querySelector('[data-lsb-title]');
      this.linkEl = this.querySelector('[data-lsb-link]');
      this.platformEl = this.querySelector('[data-lsb-platform]');
      this.platformIcons = this.querySelectorAll('[data-lsb-platform-icon]');

      this.apiUrl = '/apps/app-api/api/getActiveLiveStreams';
      this.bannerId = (this.dataset.bannerId || '').trim();
      this.shopDomain = this.dataset.shopDomain || '';
      this.fallbackText = (this.dataset.fallbackText || '').trim();
      this.fallbackUrl = (this.dataset.fallbackUrl || '').trim();
      this.openInNewTab = this.dataset.openInNewTab !== 'false';
      this.usePlatformBranding = this.dataset.usePlatformBranding !== 'false';
      this.isDesignMode = this.dataset.designMode === 'true';

      // Stay hidden until a valid live stream is confirmed
      this.hide();
      this.init();
    }

    disconnectedCallback() {
      // No timers/listeners to clean up currently
    }

    init() {
      // app_only / web_only are handled by global CSS in mobile-app-scripts
      // (.app-only / .web-only + html.is-mobile-app). Do not override display here.

      if (!this.bannerId) {
        if (this.isDesignMode) {
          this.showPreview();
        } else {
          this.hide();
        }
        return;
      }

      this.fetchStream()
        .then((data) => this.handleResponse(data))
        .catch(() => {
          this.hide();
        });
    }

    async fetchStream() {
      // App proxy: /apps/app-api/* -> https://resin-rockers--development.gadget.app/*
      const params = new URLSearchParams({
        bannerId: this.bannerId,
      });

      if (this.shopDomain) {
        params.set('shopDomain', this.shopDomain);
      }

      const response = await fetch(`${this.apiUrl}?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (err) {
        payload = null;
      }

      // Return JSON even for 404 so handleResponse can hide the banner
      if (payload && typeof payload === 'object') {
        return payload;
      }

      if (!response.ok) {
        throw new Error(`Live stream request failed (${response.status})`);
      }

      throw new Error('Live stream response was empty');
    }

    handleResponse(payload) {
      // Hide completely when stream is missing (e.g. statusCode 404)
      if (!payload || payload.statusCode !== 200) {
        this.hide();
        return;
      }

      const data = payload.data;
      if (!data) {
        this.hide();
        return;
      }

      const metadata = data.metadata || {};

      // Show whenever response data exists and bannerEnabled is true
      if (!metadata.bannerEnabled) {
        this.hide();
        return;
      }

      const title = (metadata.bannerTitle || '').trim() || this.fallbackText;
      const url = (metadata.liveStreamUrl || '').trim() || this.fallbackUrl;
      const platform = this.normalizePlatform(metadata.platform);

      this.render({ title, url, platform });
    }

    normalizePlatform(value) {
      const platform = String(value || '').trim().toLowerCase();
      if (LiveStreamBanner.PLATFORM_BRANDS[platform]) return platform;
      return '';
    }

    render({ title, url, platform }) {
      if (this.titleEl) {
        this.titleEl.textContent = title || this.fallbackText || '';
      }

      this.setPlatform(platform);
      this.updateLink(url);
      this.show();
    }

    setPlatform(platform) {
      const normalized = this.normalizePlatform(platform);

      Object.keys(LiveStreamBanner.PLATFORM_BRANDS).forEach((key) => {
        this.classList.remove(`platform-${key}`);
      });

      if (this.platformIcons && this.platformIcons.length) {
        this.platformIcons.forEach((icon) => {
          icon.hidden = true;
        });
      }

      if (!normalized) {
        if (this.platformEl) this.platformEl.hidden = true;
        this.removeAttribute('data-platform');
        this.classList.toggle('uses-platform-branding', false);
        return;
      }

      this.classList.add(`platform-${normalized}`);
      this.dataset.platform = normalized;

      if (this.platformIcons && this.platformIcons.length) {
        this.platformIcons.forEach((icon) => {
          icon.hidden = icon.dataset.lsbPlatformIcon !== normalized;
        });
      }

      if (this.platformEl) {
        this.platformEl.hidden = false;
        this.platformEl.setAttribute('aria-label', normalized);
      }

      this.classList.toggle('uses-platform-branding', this.usePlatformBranding);
    }

    updateLink(url) {
      const href = (url || '').trim();

      if (!href) {
        if (this.linkEl) {
          this.linkEl.hidden = true;
          this.linkEl.removeAttribute('href');
        }
        this.classList.remove('is-clickable');
        return;
      }

      if (!this.linkEl) return;

      this.linkEl.hidden = false;
      this.linkEl.href = href;
      this.linkEl.setAttribute('aria-label', this.titleEl?.textContent || 'Watch live stream');

      if (this.openInNewTab) {
        this.linkEl.target = '_blank';
        this.linkEl.rel = 'noopener noreferrer';
      } else {
        this.linkEl.removeAttribute('target');
        this.linkEl.removeAttribute('rel');
      }

      this.classList.add('is-clickable');
    }

    showPreview() {
      if (this.titleEl) {
        this.titleEl.textContent = this.fallbackText || 'Hey, We are live.';
      }
      // Platform always comes from the API; preview shows text/link only
      this.setPlatform('');
      this.updateLink(this.fallbackUrl);
      this.show();
    }

    show() {
      // API visibility only — device (app-only/web-only) stays on the
      // .live-stream-banner__device wrapper via global mobile-app-scripts CSS.
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.toggleSectionReady(true);
      document.body.classList.add('live-stream-banner--visible');
    }

    hide() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.toggleSectionReady(false);
      document.body.classList.remove('live-stream-banner--visible');
    }

    toggleSectionReady(ready) {
      const section = this.closest('.shopify-section');
      if (!section) return;
      // Class-only — no inline display, so global .app-only / .web-only still win
      section.classList.toggle('live-stream-banner-section--ready', ready);
    }
  }

  customElements.define('live-stream-banner', LiveStreamBanner);
  window.LiveStreamBanner = LiveStreamBanner;
}
